import {
  AnchorUnitEnum,
  GeneratedPricing,
  GenerationModeEnum,
  GenerationModeOption,
  GroupedRate,
  PricingAnchor,
  PricingConfiguration,
  PricingConfiguratorState,
  PricingPoint,
  RateTypeEnum,
  RoundModeEnum,
  RoundingOptions,
} from "./pricing-configurator.types";
import { FeeConfigStatusEnum } from "./fee-config.type";
import { ParkingRateStatusEnum, ParkingRateType, PricingPointType } from "./payment-zone-area.type";

export const CURRENCY_SUBUNIT = 100;

/** Convert a major unit amount (euros) to minor units (centimes) */
export const toMinorUnit = (amount: number): number => Math.round(amount * CURRENCY_SUBUNIT);

/** Convert a minor unit amount (centimes) to major units (euros) */
export const toMajorUnit = (amount: number): number => amount / CURRENCY_SUBUNIT;

export const DEFAULT_PRICING_CONFIG: PricingConfiguratorState = {
  anchors: [
    { duration: 15, priceAmount: 5.0 },
    { duration: 30, priceAmount: 9.5 },
    { duration: 60, priceAmount: 18.0 },
    { duration: 120, priceAmount: 34.0 },
  ],
  generationMode: GenerationModeEnum.TIME_STEP,
  timeStep: 5,
  priceStep: 0.1,
  roundTo: 0.1,
  roundMode: RoundModeEnum.NEAREST,
  keepAnchorsExact: true,
};

export const DEFAULT_LONG_PRICING_CONFIG: PricingConfiguratorState = {
  anchors: [
    { duration: 1, priceAmount: 5.0, unit: AnchorUnitEnum.DAY },
    { duration: 1, priceAmount: 25.0, unit: AnchorUnitEnum.WEEK },
    { duration: 1, priceAmount: 80.0, unit: AnchorUnitEnum.MONTH },
  ],
  generationMode: GenerationModeEnum.NONE,
  timeStep: 1,
  priceStep: 0.1,
  roundTo: 0.1,
  roundMode: RoundModeEnum.NEAREST,
  keepAnchorsExact: true,
};

export const GENERATION_MODES: GenerationModeOption[] = [
  {
    value: GenerationModeEnum.NONE,
    title: "Aucune génération",
    description: "Utiliser uniquement les points d'ancrage",
  },
  {
    value: GenerationModeEnum.TIME_STEP,
    title: "Intervalle de temps",
    description: "Interpolation linéaire par durée",
  },
  {
    value: GenerationModeEnum.PRICE_STEP,
    title: "Intervalle de prix",
    description: "Incréments de prix fixes",
  },
];

export const ROUNDING_OPTIONS_VALUES: { value: number; label: string }[] = [
  { value: 0.01, label: "0.01 €" },
  { value: 0.05, label: "0.05 €" },
  { value: 0.1, label: "0.10 €" },
  { value: 0.5, label: "0.50 €" },
  { value: 1.0, label: "1.00 €" },
];

export const ROUND_MODES: { value: RoundModeEnum; label: string }[] = [
  { value: RoundModeEnum.NEAREST, label: "Au plus proche" },
  { value: RoundModeEnum.UP, label: "Arrondi supérieur" },
  { value: RoundModeEnum.DOWN, label: "Arrondi inférieur" },
];

export const interpolate = (x: number, x1: number, y1: number, x2: number, y2: number): number => {
  return y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
};

export const generateTimeStepPoints = (
  sortedAnchors: PricingAnchor[],
  timeStep: number,
  applyRounding: (priceAmount: number, isAnchor: boolean) => number,
): PricingPoint[] => {
  const points: PricingPoint[] = [];
  const anchorDurations = new Set(sortedAnchors.map((a) => a.duration));

  const minDuration = sortedAnchors[0].duration;
  const maxDuration = sortedAnchors[sortedAnchors.length - 1].duration;

  for (let duration = minDuration; duration <= maxDuration; duration += timeStep) {
    const isAnchor = anchorDurations.has(duration);
    let priceAmount: number;

    if (isAnchor) {
      priceAmount = sortedAnchors.find((a) => a.duration === duration)!.priceAmount;
    } else {
      let i = 0;
      while (i < sortedAnchors.length && sortedAnchors[i].duration < duration) {
        i++;
      }
      if (i === 0) {
        priceAmount = sortedAnchors[0].priceAmount;
      } else if (i >= sortedAnchors.length) {
        priceAmount = sortedAnchors[sortedAnchors.length - 1].priceAmount;
      } else {
        const prevAnchor = sortedAnchors[i - 1];
        const nextAnchor = sortedAnchors[i];
        priceAmount = interpolate(
          duration,
          prevAnchor.duration,
          prevAnchor.priceAmount,
          nextAnchor.duration,
          nextAnchor.priceAmount,
        );
      }
    }
    points.push({ duration, priceAmount: applyRounding(priceAmount, isAnchor), isAnchor });
  }

  const lastAnchor = sortedAnchors[sortedAnchors.length - 1];
  if (!points.find((p) => p.duration === lastAnchor.duration)) {
    points.push({
      duration: lastAnchor.duration,
      priceAmount: applyRounding(lastAnchor.priceAmount, true),
      isAnchor: true,
    });
  }

  return points;
};

export const generatePriceStepPoints = (
  sortedAnchors: PricingAnchor[],
  priceStep: number,
  applyRounding: (priceAmount: number, isAnchor: boolean) => number,
): PricingPoint[] => {
  const points: PricingPoint[] = [];

  for (let i = 0; i < sortedAnchors.length; i++) {
    const currentAnchor = sortedAnchors[i];
    const nextAnchor = sortedAnchors[i + 1];

    points.push({
      duration: currentAnchor.duration,
      priceAmount: applyRounding(currentAnchor.priceAmount, true),
      isAnchor: true,
    });

    if (nextAnchor) {
      const priceDiff = nextAnchor.priceAmount - currentAnchor.priceAmount;
      const durationDiff = nextAnchor.duration - currentAnchor.duration;
      const numSteps = Math.floor(priceDiff / priceStep);

      for (let step = 1; step < numSteps; step++) {
        const priceAmount = currentAnchor.priceAmount + step * priceStep;
        const duration = currentAnchor.duration + (step / numSteps) * durationDiff;

        points.push({
          duration: Math.round(duration),
          priceAmount: applyRounding(priceAmount, false),
          isAnchor: false,
        });
      }
    }
  }
  return points;
};

const applyRounding = (priceAmount: number, isAnchor: boolean, opts: RoundingOptions): number => {
  if (isAnchor && opts.keepAnchorsExact) return priceAmount;
  switch (opts.roundMode) {
    case "up":
      return Math.ceil(priceAmount / opts.roundTo) * opts.roundTo;
    case "down":
      return Math.floor(priceAmount / opts.roundTo) * opts.roundTo;
    default:
      return Math.round(priceAmount / opts.roundTo) * opts.roundTo;
  }
};

/** Preview Core generation logic  */
export const generatePricingPoints = (
  anchors: PricingAnchor[],
  mode: GenerationModeEnum,
  stepValue: number | undefined,
  rounding: RoundingOptions = { roundTo: 0.1, roundMode: RoundModeEnum.NEAREST, keepAnchorsExact: true },
): GeneratedPricing => {
  const sortedAnchors = [...anchors].sort((a, b) => a.duration - b.duration);

  const hasDuplicates = sortedAnchors.some((a, i) => i > 0 && a.duration === sortedAnchors[i - 1].duration);
  if (hasDuplicates) {
    return { points: [], warnings: ["Les durées des points d'ancrage doivent être uniques."] };
  }

  const round = (price: number, isAnchor: boolean) => applyRounding(price, isAnchor, rounding);
  let points: PricingPoint[];

  switch (mode) {
    case GenerationModeEnum.TIME_STEP:
      points = generateTimeStepPoints(sortedAnchors, stepValue ?? DEFAULT_PRICING_CONFIG.timeStep, round);
      break;
    case GenerationModeEnum.PRICE_STEP:
      points = generatePriceStepPoints(sortedAnchors, stepValue ?? DEFAULT_PRICING_CONFIG.priceStep, round);
      break;
    case GenerationModeEnum.NONE:
    default:
      points = sortedAnchors.map((anchor) => ({
        duration: anchor.duration,
        priceAmount: round(anchor.priceAmount, true),
        isAnchor: true,
      }));
      break;
  }

  return { points, warnings: [] };
};

/** Generate compiled pricing points from a stored config for publish */
export const generateCompiledPricingPoints = (config: PricingConfiguration): Omit<PricingPoint, "isAnchor">[] => {
  const rawAnchors = config.anchors ?? [];
  if (rawAnchors.length === 0) return [];

  const anchorsInEuros = rawAnchors.map((a) => ({
    ...a,
    priceAmount: toMajorUnit(a.priceAmount),
  }));

  const { points } = generatePricingPoints(
    anchorsInEuros,
    config.generationMode.mode,
    config.generationMode.stepValue,
    config.roundingRules,
  );

  return points.map((p) => ({
    duration: p.duration,
    priceAmount: toMinorUnit(p.priceAmount),
  }));
};

export const compilePricingPoints = (rate: ParkingRateType): PricingPointType[] => {
  if (!rate.pricingConfiguration) return [];
  if (rate.rateType === RateTypeEnum.LONG) {
    return (rate.pricingConfiguration.anchors ?? []).map(({ duration, unit, priceAmount }) => ({
      duration,
      unit,
      priceAmount,
    }));
  }
  return generateCompiledPricingPoints(rate.pricingConfiguration);
};

const statusPriority = (status?: ParkingRateStatusEnum) => {
  if (status === ParkingRateStatusEnum.DRAFT) return 0;
  if (status === ParkingRateStatusEnum.ENABLED) return 1;
  return 2;
};

export const groupRatesByLogicalId = (parkingRates: ParkingRateType[]): GroupedRate[] => {
  const groups = new Map<string, ParkingRateType[]>();

  // Group rates by logicalRateId
  for (const rate of parkingRates) {
    const key = rate.logicalRateId ?? String(rate.id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(rate);
  }

  // Sort each group by version and determine current vs previous versions
  return Array.from(groups.entries()).map(([logicalRateId, versions]) => {
    versions.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));

    const currentRate = [...versions].sort((a, b) => statusPriority(a.status) - statusPriority(b.status))[0];

    const currentVersions = versions.filter(
      (v) => v.status === ParkingRateStatusEnum.DRAFT || v.status === ParkingRateStatusEnum.ENABLED,
    );
    const previousVersions = versions.filter((v) => v.status === ParkingRateStatusEnum.DISABLED);

    return { logicalRateId, currentRate, allVersions: versions, currentVersions, previousVersions };
  });
};

export const getDisplayName = (rate: ParkingRateType) => {
  if (!rate.name || !Array.isArray(rate.name) || rate.name.length === 0) return "Sans nom";
  const frName = rate.name.find((item) => item.locale === "fr");
  const enName = rate.name.find((item) => item.locale === "en");
  return frName?.text || enName?.text || rate.name[0]?.text || "Sans nom";
};

export const getVersionLabel = (rate: ParkingRateType) => {
  const versionNum = rate.version != null ? `v${rate.version}` : "brouillon";
  const restored = rate.restoredFromVersion != null ? ` (restoré depuis v${rate.restoredFromVersion})` : "";
  const date = rate.publishedAt ? ` - ${new Date(rate.publishedAt).toLocaleDateString("fr-FR")}` : "";
  return `${versionNum}${restored}${date}`;
};

export const getStatusBadge = (status?: ParkingRateStatusEnum | FeeConfigStatusEnum) => {
  if (status === ParkingRateStatusEnum.ENABLED || status === FeeConfigStatusEnum.ENABLED) {
    return {
      label: "Actif",
      className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    };
  }
  if (status === ParkingRateStatusEnum.DRAFT || status === FeeConfigStatusEnum.DRAFT) {
    return {
      label: "Brouillon",
      className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    };
  }
  return {
    label: "Désactivé",
    className: "bg-muted text-muted-foreground",
  };
};

export const ANCHOR_UNIT_OPTIONS: { value: AnchorUnitEnum; label: string }[] = [
  { value: AnchorUnitEnum.DAY, label: "Jour" },
  { value: AnchorUnitEnum.WEEK, label: "Semaine" },
  { value: AnchorUnitEnum.MONTH, label: "Mois" },
];

export const AI_PROMPT = `J'aimerais que tu me régénères les tableaux du PDF par zone (zone orange, zone verte,
zone puces, etc.), dans le même ordre que dans le document original. Chaque tableau ne
doit plus être à trois colonnes (Horaire, Voiture, PL+3,5T) mais divisé en deux tableaux
séparés : (Horaire, Voiture) pour le premier, et (Horaire, PL+3,5T) pour le deuxième.
Fais ça pour chaque zone.

Convertis également toutes les durées en minutes, en gardant l'unité "min" après chaque
nombre (exemple : "1h30" devient "90 min", "2h15" devient "135 min", "45 min" reste "45 min").

Respecte scrupuleusement toutes les valeurs numériques du document source, sans arrondir
ni inventer de données manquantes. Présente le résultat sous forme de tableaux Markdown,
avec un titre par zone.`;
